/**
 * Cookie asosidagi super app hostlar (Click, Zoomrad, Payme, …) bir xil shaklda
 * ishlaydi: super app o'zining domeniga ochadi, `*-web-session` nomli cookie ni
 * yozadi, mini app shu cookie ni o'qib backenddagi `/MiniApp/<Vendor>Login`
 * endpointiga yuboradi. Shu pattern factory orqali yagonalashtirilgan.
 *
 * Yangi vendor qo'shish — bu fayl ga teginmasdan, faqat `defineCookieHost({...})`
 * ni chaqirib `src/hosts/index.ts` ga ro'yxatga olish kifoya.
 */

import { apiPost } from "@/api/client";
import { setToken } from "@/lib/auth";
import { getCookie } from "@/lib/cookies";
import {
  DEFAULT_LANG,
  LOCAL_LANG_KEY,
  normalizeLangKey,
  type AppLang,
} from "@/lib/env";
import type { BrandId } from "@/config/uiTokens";
import type { HostAdapter, HostId, LoginResponse, ThemeMode } from "./types";
import { pollFor } from "./wait";

export interface CookieHostConfig {
  id: HostId;
  brandId: BrandId;
  /** Hosting super app ning domeni (mas. "click.insonuchun.uz") */
  hostnames: readonly string[];
  /** Backenddagi login endpoint (mas. "/MiniApp/ClickLogin") */
  loginEndpoint: string;
  /** Sessiya cookie nomlari — birinchi topilgani ishlatiladi */
  sessionCookies: readonly string[];
  /** Host taqdim etadigan til cookie (ixtiyoriy) */
  langCookie?: string;
  /** Host taqdim etadigan tema cookie (ixtiyoriy) */
  themeCookie?: string;
  /**
   * iOS webview da cookie kechikishi mumkin — shu vaqt davomida kutamiz.
   * Default 4000ms.
   */
  sessionTimeoutMs?: number;
}

/** Bir nechta cookie nomidan birinchi non-null qiymatni qaytaradi */
function readFirstCookie(names: readonly string[]): string | null {
  for (const name of names) {
    const value = getCookie(name);
    if (value) return value;
  }
  return null;
}

export function defineCookieHost(config: CookieHostConfig): HostAdapter {
  const hostnameSet = new Set(config.hostnames);

  return {
    id: config.id,
    brandId: config.brandId,

    match() {
      if (typeof window === "undefined") return false;
      return hostnameSet.has(window.location.hostname);
    },

    async login(): Promise<LoginResponse> {
      // iOS WKWebView da cookie modul yuklanish vaqtida hali tayyor bo'lmasligi mumkin.
      // Shuning uchun darhol o'qiymiz, bo'lmasa polling bilan kutamiz.
      const session = await pollFor(
        () => readFirstCookie(config.sessionCookies),
        config.sessionTimeoutMs
      );

      // `session` null bo'lsa ham serverga jo'natamiz — backend o'zining
      // xato javobi orqali aniq feedback beradi (toast ApiError orqali).
      const payload = {
        webSession: session,
        activate: true,
        lang: (localStorage.getItem(LOCAL_LANG_KEY) as AppLang | null) ?? DEFAULT_LANG,
      };

      const data = await apiPost<LoginResponse>(config.loginEndpoint, payload);
      setToken(data.token);
      return data;
    },

    getPreferredLang() {
      return config.langCookie ? normalizeLangKey(getCookie(config.langCookie)) : null;
    },

    getPreferredTheme() {
      if (!config.themeCookie) return null;
      const raw = getCookie(config.themeCookie);
      return raw === "dark" || raw === "light" ? (raw as ThemeMode) : null;
    },
  };
}
